import { type GoogleAddressPrediction, type GoogleAddressSuggestion, PlaceType } from '@global-types/googleAddress.ts';
import { Logger } from '@tools/logger/logger';
import { flashBagError } from '@tools/flashbag.ts';
import { useAppDataStore } from '@stores/useAppDataStore.ts';
import { getGooglePlaceType } from '@tools/address-utils.ts';
import { Requests } from '@tools/requests';
import { URL_GEOCODE_ADDRESS_FROM_COORDINATES } from '@tools/routes/routes';

export interface ExtendedGoogleAddressSuggestion extends GoogleAddressSuggestion {
  type: PlaceType | null;
  mainText: string;
  secondaryText: string;
}

interface GoogleAutocompleteRawResponse {
  suggestions: {
    placePrediction: GoogleAddressPrediction;
  }[];
}

export interface GoogleAutocompleteParams {
  placeTypes?: PlaceType[];
}

export const getAddresses = async (query: string, params: GoogleAutocompleteParams = {}): Promise<ExtendedGoogleAddressSuggestion[]> => {
  const {
    googleApiKey,
    sessionToken,
  } = useAppDataStore();

  if (!googleApiKey) {
    return [];
  }

  const googleMask: string[] = [
    'suggestions.placePrediction.placeId',
    'suggestions.placePrediction.text.text',
    'suggestions.placePrediction.structuredFormat.mainText.text',
    'suggestions.placePrediction.structuredFormat.secondaryText.text',
    'suggestions.placePrediction.types',
  ] as const;

  try {
    const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': googleApiKey,
        'X-Goog-FieldMask': googleMask.join(','),
      },
      body: JSON.stringify({
        input: query,
        sessionToken,
        includedRegionCodes: [ 'FR', 'BE', 'MC', 'LU' ],
        includedPrimaryTypes: params.placeTypes,
        languageCode: 'fr',
      }),
    });

    const json = await response.json() as GoogleAutocompleteRawResponse;

    return (json?.suggestions ?? [])
      .map((suggestion) => suggestion.placePrediction)
      .filter((prediction) => !!prediction.placeId)
      .map((prediction) => ({
        placeId: prediction.placeId,
        type: getGooglePlaceType(prediction.types),
        text: prediction.text?.text ?? '',
        mainText: prediction.structuredFormat?.mainText?.text ?? '',
        secondaryText: prediction.structuredFormat?.secondaryText?.text ?? '',
      }))
      .filter((suggestion) => suggestion.placeId && suggestion.text);
  } catch (err: unknown) {
    Logger.error(err);
    flashBagError();
  }

  return [];
};

interface GoogleGeocodingResult {
  place_id: string;
  formatted_address: string;
  types: string[];
}

export const getAddressByCoordinates = async (
  lat: number,
  lng: number,
): Promise<ExtendedGoogleAddressSuggestion | null> => {
  const { googleApiKey: apiKey } = useAppDataStore();

  if (!apiKey) {
    return null;
  }

  try {
    const { data } = await Requests.get(
      URL_GEOCODE_ADDRESS_FROM_COORDINATES,
      {
        params: {
          lat,
          lng,
        },
      },
    );

    const preferredTypes: PlaceType[] = [
      PlaceType.STREET_ADDRESS,
      PlaceType.ROUTE,
      PlaceType.CITY,
      PlaceType.POSTAL_CODE,
      PlaceType.DISTRICT,
      PlaceType.DISTRICT_LEVEL_1,
    ] as const;

    const preferredPlace = (data as GoogleGeocodingResult[])
      .find((result) => result.types.some((type: string) => preferredTypes.includes(type as PlaceType)));

    const {
      place_id: placeId,
      formatted_address: formattedAddress,
      types,
    } = preferredPlace ?? data.results[0];

    return {
      placeId,
      text: formattedAddress,
      mainText: formattedAddress.split(',')[0],
      secondaryText: formattedAddress.split(',').slice(1).join(',').trim(),
      type: getGooglePlaceType(types),
    };
  } catch (err: unknown) {
    Logger.error(err);
    flashBagError();
  }

  return null;
};
